import _isEmpty from 'lodash/isEmpty';
/**
 * Utility to determine filter hierarchy and filter data
 * Priority order:
 * 1) Strong Attribute filter
 * 2) Truck/SUV Bodystyle substyle (Truck cab size filter, SUV 3rd row filter)
 * 3) Vehicle Features
 * 4) Suggested filter
 */

// Helper to get a unique key for the filter being shown
export const getFilterSessionKey = ({
    isStrongAttribute,
    firstStrongAttribute,
    isTruckCard,
    isSUVCard,
    isSuggestedFilter,
    firstSuggestedFilter,
}) => {
    if (isStrongAttribute && firstStrongAttribute) {
        const strongAttributeCodes = Object.values(firstStrongAttribute.codes || {}).flat();
        return `${strongAttributeCodes.join(',')}`;
    }
    if (isTruckCard) return 'truck';
    if (isSUVCard) return 'suv';
    if (isSuggestedFilter && firstSuggestedFilter) {
        const allSuggestedCodes = Object.values(firstSuggestedFilter.codes || {}).flat();
        return `${allSuggestedCodes?.join(',')}`;
    }
    return null;
};

export const hasSeenFilter = (sessionKey) => {
    if (!sessionKey) return false;
    try {
        const seen = JSON.parse(sessionStorage.getItem('inlineFilterSeen') || '[]');
        return seen.includes(sessionKey);
    } catch {
        return false;
    }
};

export const markFilterAsSeen = (sessionKey) => {
    if (!sessionKey) return;
    try {
        const seen = JSON.parse(sessionStorage.getItem('inlineFilterSeen') || '[]');
        if (!seen.includes(sessionKey)) {
            seen.push(sessionKey);
            sessionStorage.setItem('inlineFilterSeen', JSON.stringify(seen));
        }
    } catch {
        // Ignore JSON parse errors
    }
};

// Helper function to check if a filter is already active/selected
export const isFilterAlreadyActive = (filter, activeFilterNames) => {
    if (!filter?.codes) {
        return false;
    }
    const codeKeys = Object.keys(filter.codes);
    return codeKeys.some((codeKey) => activeFilterNames.includes(codeKey));
};

// Find the first inactive and unseen filter
export const findFirstInactiveUnseenFilter = (filters, activeFilterNames, getSessionKeyFn, filterType) => {
    if (!filters || filters.length === 0) {
        return null;
    }
    return filters.find((filter) => {
        if (isFilterAlreadyActive(filter, activeFilterNames)) return false;
        // Compose a minimal candidate for session key
        let candidate;
        if (filterType === 'strongAttribute') {
            candidate = { isStrongAttribute: true, firstStrongAttribute: filter };
        } else if (filterType === 'suggested') {
            candidate = { isSuggestedFilter: true, firstSuggestedFilter: filter };
        } else {
            candidate = {};
        }
        const sessionKey = getSessionKeyFn(candidate);
        return !hasSeenFilter(sessionKey);
    }) || null;
};

export const getFilterHierarchy = ({
    activeFilterValues = [],
    suggestedFilters = [],
    strongAttributeFilters = [],
    isTruckSelected = false,
    isSUVSelected = false,
    featureFilterOptions = null,
    featureFilterValues = [],
    cabSizeOptions = [],
    cabSizeValues = [],
}) => {
    // Helper to get active filter names
    const activeFilterNames = activeFilterValues?.map((filter) => filter.filterName) || [];

    // Find first inactive and unseen filters
    const firstStrongAttribute = findFirstInactiveUnseenFilter(strongAttributeFilters, activeFilterNames, getFilterSessionKey, 'strongAttribute');
    const firstSuggestedFilter = findFirstInactiveUnseenFilter(suggestedFilters, activeFilterNames, getFilterSessionKey, 'suggested');

    // Priority 1: Strong Attributes
    const isStrongAttribute = !!firstStrongAttribute;

    // Priority 2: Truck/SUV Bodystyle substyle
    const thirdRowFilterOptions = featureFilterOptions?.options?.find((option) => option.value === '1306');
    const thirdRowApplied = featureFilterValues.includes('1306');
    const cabSizeApplied = cabSizeValues.length > 0;

    const shouldShowTruckCard = !firstStrongAttribute && isTruckSelected && !cabSizeApplied && !_isEmpty(cabSizeOptions);
    const truckSessionKey = shouldShowTruckCard ? getFilterSessionKey({ isTruckCard: true }) : null;
    const isTruckCard = shouldShowTruckCard && !hasSeenFilter(truckSessionKey);

    const shouldShowSUVCard = !firstStrongAttribute && !isTruckCard
        && isSUVSelected && !thirdRowApplied && !_isEmpty(thirdRowFilterOptions);
    const suvSessionKey = shouldShowSUVCard ? getFilterSessionKey({ isSUVCard: true }) : null;
    const isSUVCard = shouldShowSUVCard && !hasSeenFilter(suvSessionKey);

    // Priority 3: Suggested Filters
    const isSuggestedFilter = !firstStrongAttribute && !isTruckCard && !isSUVCard && !!firstSuggestedFilter;

    return {
        // Priority flags
        isStrongAttribute,
        isTruckCard,
        isSUVCard,
        isSuggestedFilter,

        // Filter data
        firstStrongAttribute,
        firstSuggestedFilter,

        // Helper values
        activeFilterNames,
        thirdRowApplied,
        cabSizeApplied,
    };
};

export default getFilterHierarchy;
